import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Plus, X, Clock } from 'lucide-react'
import type { Medication, MedicationForm, MeasurementUnit } from '@/types/medicamento'

const FORMA_LABELS: Record<MedicationForm, string> = {
  comprimido: 'Comprimido',
  liquido: 'Líquido',
  inalatorio_spray: 'Inalatório / Spray',
  aplicacao: 'Aplicação',
  triturado_diluido: 'Triturado / Diluído'
}

const UNIDADE_POR_FORMA: Record<MedicationForm, MeasurementUnit[]> = {
  comprimido: ['inteiro', 'fracionado_1_2', 'fracionado_1_4'],
  liquido: ['gotas', 'ml'],
  inalatorio_spray: ['jatos'],
  aplicacao: ['ampola', 'ml'],
  triturado_diluido: []
}

const UNIDADE_LABELS: Record<MeasurementUnit, string> = {
  inteiro: 'Comprimido(s) inteiro(s)',
  fracionado_1_2: 'Comprimido(s) 1/2',
  fracionado_1_4: 'Comprimido(s) 1/4',
  gotas: 'Gotas',
  ml: 'mL',
  jatos: 'Jatos',
  ampola: 'Ampola(s)'
}

const RECORRENCIA_LABELS: Record<string, string> = {
  daily: 'Todos os dias',
  range: 'Período específico',
  once: 'Apenas uma vez'
}

interface MedicamentoFormProps {
  initialData?: Medication
  onSave: (med: Medication) => void
  onCancel: () => void
}

export function MedicamentoForm({ initialData, onSave, onCancel }: MedicamentoFormProps) {
  const [form, setForm] = useState<Medication>({
    name: initialData?.name || '',
    instructions: initialData?.instructions || '',
    recurrence: initialData?.recurrence || 'daily',
    recurrenceStart: initialData?.recurrenceStart || '',
    recurrenceEnd: initialData?.recurrenceEnd || '',
    schedules: initialData?.schedules?.length ? [...initialData.schedules] : ['08:00'],
    medicationForm: initialData?.medicationForm,
    measurementUnit: initialData?.measurementUnit,
    quantityPerDose: initialData?.quantityPerDose,
    doseTotalMg: initialData?.doseTotalMg,
    dilutionVolumeMl: initialData?.dilutionVolumeMl,
    reuseMode: initialData?.reuseMode,
  })

  const addSchedule = () => {
    setForm(prev => ({ ...prev, schedules: [...prev.schedules, '12:00'] }))
  }

  const updateSchedule = (idx: number, value: string) => {
    setForm(prev => ({
      ...prev,
      schedules: prev.schedules.map((s, i) => i === idx ? value : s)
    }))
  }

  const removeSchedule = (idx: number) => {
    if (form.schedules.length <= 1) return
    setForm(prev => ({ ...prev, schedules: prev.schedules.filter((_, i) => i !== idx) }))
  }

  const handleSave = () => {
    if (!form.name.trim()) return
    onSave({ ...form, name: form.name.trim() })
  }

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Nome do Medicamento *</Label>
          <Input
            placeholder="Ex: Losartana"
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            maxLength={100}
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Instruções (opcional)</Label>
          <Input
            placeholder="Ex: Tomar com água, após o café"
            value={form.instructions}
            onChange={e => setForm(p => ({ ...p, instructions: e.target.value }))}
            maxLength={200}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Recorrência</Label>
          <Select value={form.recurrence} onValueChange={(v: any) => setForm(p => ({ ...p, recurrence: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(RECORRENCIA_LABELS).map(([k, l]) => (
                <SelectItem key={k} value={k}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

      </div>

      {form.recurrence === 'range' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Data de início</Label>
            <Input type="date" value={form.recurrenceStart || ''} onChange={e => setForm(p => ({ ...p, recurrenceStart: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Data de término</Label>
            <Input type="date" value={form.recurrenceEnd || ''} onChange={e => setForm(p => ({ ...p, recurrenceEnd: e.target.value }))} />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs font-semibold flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" /> Horários
        </Label>
        <div className="space-y-2">
          {form.schedules.map((time, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input type="time" value={time} onChange={e => updateSchedule(idx, e.target.value)} className="w-32" />
              {form.schedules.length > 1 && (
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeSchedule(idx)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={addSchedule}>
            <Plus className="h-3 w-3" /> Adicionar horário
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Forma farmacêutica</Label>
          <Select value={form.medicationForm || ''} onValueChange={(v: MedicationForm) => setForm(p => ({ ...p, medicationForm: v, measurementUnit: undefined }))}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {(Object.keys(FORMA_LABELS) as MedicationForm[]).map(k => (
                <SelectItem key={k} value={k}>{FORMA_LABELS[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {form.medicationForm && form.medicationForm !== 'triturado_diluido' && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Unidade de medida</Label>
            <Select value={form.measurementUnit || ''} onValueChange={(v: MeasurementUnit) => setForm(p => ({ ...p, measurementUnit: v }))}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {UNIDADE_POR_FORMA[form.medicationForm].map(u => (
                  <SelectItem key={u} value={u}>{UNIDADE_LABELS[u]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {(form.measurementUnit || form.medicationForm === 'triturado_diluido') && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {form.medicationForm === 'triturado_diluido' ? 'Dose prescrita (mg)' : 'Quantidade por dose'}
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder={form.medicationForm === 'triturado_diluido' ? 'Ex: 2.5' : 'Ex: 1'}
              value={form.quantityPerDose || ''}
              onChange={e => setForm(p => ({ ...p, quantityPerDose: parseFloat(e.target.value) || 0 }))}
            />
          </div>
        )}
      </div>

      {form.medicationForm === 'triturado_diluido' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Dose total do comprimido (mg)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Ex: 10"
              value={form.doseTotalMg || ''}
              onChange={e => setForm(p => ({ ...p, doseTotalMg: parseFloat(e.target.value) || 0 }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Volume de diluição (mL)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Ex: 10"
              value={form.dilutionVolumeMl || ''}
              onChange={e => setForm(p => ({ ...p, dilutionVolumeMl: parseFloat(e.target.value) || 0 }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Modo de uso</Label>
            <Select value={form.reuseMode || ''} onValueChange={(v: 'descartar' | 'reaproveitar') => setForm(p => ({ ...p, reuseMode: v }))}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="descartar">Usar e descartar (1 comprimido/dose)</SelectItem>
                <SelectItem value="reaproveitar">Reaproveitar solução</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {form.medicationForm === 'triturado_diluido' && (form.doseTotalMg || 0) > 0 && (form.quantityPerDose || 0) > 0 && (
        <div className="text-xs text-blue-600 font-medium">
          {Math.floor((form.doseTotalMg || 0) / (form.quantityPerDose || 0))} doses por comprimido
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" className="flex-1" onClick={handleSave} disabled={!form.name.trim()}>
          {initialData ? 'Atualizar' : 'Adicionar'} Medicamento
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}
